import React from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '@schuettflix/planum-react';
import { useZodForm } from '@/shared/hooks/useZodForm.ts';
import { useVehicleClasses } from '@/clients/vehicleClasses/useVehicleClasses.ts';
import { useWizardPanelContext, WizardFooter } from '@/shared/components/Wizard.tsx';
import { DeleteAllProductsButton } from '@/shared/components/DeleteAllProductsButton.tsx';
import { productTypeToFreightTypeMap } from '@/shared/domain/product/productTypeToFreightTypeMap.ts';
import { isTransportProduct } from '@/shared/domain/product/isTransportProduct.ts';
import { DEFAULT_VEHICLE_CLASS_IDENTIFIER } from '../constants/vehicle-class.ts';
import { cn } from '@/shared/utils/cn.ts';
import { SwitchFieldInput } from '@/shared/inputs/SwitchFieldInput.tsx';
import {
    AutomaticProductsFormValues,
    getAutomaticProductsFormSchema,
} from '../schema/getAutomaticProductsFormSchema.ts';
import { OrderProductDetailsFieldset } from '../fieldsets/OrderProductDetailsFieldset.tsx';
import { OrderPurchaseMaterialFieldset } from '../fieldsets/OrderPurchaseMaterialFieldset.tsx';
import { OrderPurchaseTransportFieldset } from '../fieldsets/OrderPurchaseTransportFieldset.tsx';
import { OrderSalesMaterialFieldset } from '../fieldsets/OrderSalesMaterialFieldset.tsx';
import { OrderSalesTransportFieldset } from '../fieldsets/OrderSalesTransportFieldset.tsx';
import { LocationsFieldset } from '../fieldsets/LocationsFieldset.tsx';
import { DispositionFieldset } from '../fieldsets/DispositionFieldset.tsx';
import { FieldsetColumn, FieldsetGroup } from '../components/Fieldset.tsx';
import { AdditionalServicesFieldset } from '../fieldsets/AdditionalServicesFieldset.tsx';
import { AutomaticProductsFormProps } from '../AutomaticProductsForm.tsx';
import { useAutomaticFormContext } from '../context/AutomaticFormContext.tsx';
import { useMarketContext } from '@/shared/context/MarketContext.tsx';
import { MARKET_CODE_TO_CURRENCY_CODE_MAP } from '@schuettflix/utils';
import { useFormToggles } from '../hooks/useFormToggles.ts';
import { useFormSubmit } from '../hooks/useFormSubmit.ts';
import { deriveQuantity } from '../utils/deriveQuantity.ts';

type OrderAutomaticProductsFormProps = Omit<
    AutomaticProductsFormProps,
    'customerOrganizationId' | 'constructionProjectId' | 'scope' | 'formRules' | 'onClearSelection'
>;

export function OrderAutomaticProductsForm({
    productType,
    isStandalone,
    pricingStrategy = 'FRANCO',
    defaultValues,
    onSubmit,
    mode,
}: OrderAutomaticProductsFormProps) {
    const { t } = useTranslation();
    const { hasOpenedPanel } = useWizardPanelContext();
    const { data: vehicleClasses } = useVehicleClasses();
    const { defaultTaxClass, formRules } = useAutomaticFormContext();
    const { marketCode } = useMarketContext();

    const defaultVehicleClass = vehicleClasses.find(vc => vc.identifier === DEFAULT_VEHICLE_CLASS_IDENTIFIER);

    const schema = getAutomaticProductsFormSchema({
        errorMessages: {
            required: t('forms.automaticProductsForm.errorMessages.required'),
            quantityMinValue: t('forms.inputs.quantity.minValue.errorMessage'),
            missingWeighingDataLocation: t('forms.merchantForm.sections.locations.addWeighingData'),
        },
        priceRules: { allowNegative: false },
        additionalServices: formRules?.additionalServices,
    });

    const methods = useZodForm<typeof schema, 'ignoreTransformedValues'>({
        schema,
        defaultValues: {
            scope: 'ORDER',
            productType,
            isStandalone,

            // Product Identifiers
            materialIdentifier: defaultValues?.materialIdentifier,
            transportIdentifier: defaultValues?.transportIdentifier,

            // Product Details FieldSet
            productName: defaultValues?.productName,
            freightType: defaultValues?.freightType ?? productTypeToFreightTypeMap[productType],
            currencyCode: defaultValues?.currencyCode ?? MARKET_CODE_TO_CURRENCY_CODE_MAP[marketCode],
            disposalPurpose: defaultValues?.disposalPurpose,
            disposalCertificateNumber: defaultValues?.disposalCertificateNumber,

            // Sales Material Fieldset
            salesMaterialQuantity: defaultValues?.salesMaterialQuantity,
            salesMaterialUnit: defaultValues?.salesMaterialUnit ?? 'TON',
            salesMaterialPrice: defaultValues?.salesMaterialPrice,
            salesMaterialTaxClassId: defaultValues?.salesMaterialTaxClassId ?? defaultTaxClass,
            showZeroSalesMaterialPrice: defaultValues?.showZeroSalesMaterialPrice ?? false,
            isSalesTransportIncluded:
                defaultValues?.isSalesTransportIncluded ??
                (pricingStrategy === 'FRANCO' || pricingStrategy === 'MERCHANT'),

            // Sales Transport Fieldset
            vehicleClass: defaultValues?.vehicleClass ?? defaultVehicleClass,
            salesTransportQuantity: defaultValues?.salesTransportQuantity,
            salesTransportUnit: defaultValues?.salesTransportUnit ?? 'TON',
            salesTransportPrice: defaultValues?.salesTransportPrice,
            salesTransportTaxClassId: defaultValues?.salesTransportTaxClassId ?? defaultTaxClass,
            showZeroSalesTransportPrice: defaultValues?.showZeroSalesTransportPrice ?? false,

            // Purchase Material Fieldset
            supplierOrganizationId: defaultValues?.supplierOrganizationId,
            purchaseMaterialQuantity: defaultValues?.purchaseMaterialQuantity,
            purchaseMaterialUnit: defaultValues?.purchaseMaterialUnit ?? 'TON',
            purchaseMaterialPrice: defaultValues?.purchaseMaterialPrice,
            purchaseMaterialTaxClassId: defaultValues?.purchaseMaterialTaxClassId ?? defaultTaxClass,
            showZeroPurchaseMaterialPrice: defaultValues?.showZeroPurchaseMaterialPrice ?? false,
            isPurchaseTransportIncluded: defaultValues?.isPurchaseTransportIncluded ?? pricingStrategy === 'MERCHANT',

            // Purchase Transport Fieldset
            carrierOrganizationId: defaultValues?.carrierOrganizationId,
            purchaseTransportQuantity: defaultValues?.purchaseTransportQuantity,
            purchaseTransportUnit: defaultValues?.purchaseTransportUnit ?? 'TON',
            purchaseTransportPrice: defaultValues?.purchaseTransportPrice,
            purchaseTransportTaxClassId: defaultValues?.purchaseTransportTaxClassId ?? defaultTaxClass,
            showZeroPurchaseTransportPrice: defaultValues?.showZeroPurchaseTransportPrice ?? false,

            // Locations FieldSet
            loadingLocation: defaultValues?.loadingLocation,
            unloadingLocation: defaultValues?.unloadingLocation,
            weighingLocation: defaultValues?.weighingLocation ?? null,

            // Seller product FieldSet
            sellerProduct: defaultValues?.sellerProduct,

            // Disposition Fieldset
            disposition: defaultValues?.disposition,
            transportNumber: isTransportProduct(productType)
                ? deriveQuantity(
                      defaultValues?.salesTransportQuantity,
                      defaultValues?.salesTransportUnit,
                      'TRANSPORT',
                      defaultValues?.vehicleClass?.payloadInTons
                  )
                : deriveQuantity(
                      defaultValues?.salesMaterialQuantity,
                      defaultValues?.salesMaterialUnit,
                      'TRANSPORT',
                      defaultValues?.vehicleClass?.payloadInTons
                  ),

            // Additional services FieldSet
            additionalServices: defaultValues?.additionalServices ?? [],
        } as Partial<AutomaticProductsFormValues>,
    });

    const { watch, register } = methods;
    const { handleFormSubmit, isSubmitDisabled } = useFormSubmit(methods, onSubmit);
    const { handleClearSelection } = useAutomaticFormContext();
    const { handlePurchaseTransportToggle } = useFormToggles(methods);

    const [isSalesTransportIncluded, isPurchaseTransportIncluded] = watch([
        'isSalesTransportIncluded',
        'isPurchaseTransportIncluded',
    ]);

    const isTransportOnly = isTransportProduct(productType);

    const showSalesTransport = !isSalesTransportIncluded || isTransportOnly;
    const showPurchaseTransport = !isPurchaseTransportIncluded || isTransportOnly;
    const showLocations = showSalesTransport || showPurchaseTransport || isSalesTransportIncluded;
    const showTransportToggle = !isTransportOnly && !hasOpenedPanel;

    return (
        <FormProvider {...methods}>
            <form
                className="@container flex min-w-0 flex-1 flex-col justify-between"
                data-test="automatic-products-form"
                onSubmit={handleFormSubmit}
            >
                <div className="flex flex-col gap-4">
                    <FieldsetGroup title={t('forms.automaticProductsForm.sections.salesPositions')}>
                        <div className="grid grid-cols-9 gap-x-2 gap-y-4">
                            <div className={cn(showTransportToggle ? 'col-span-8' : 'col-span-9')}>
                                <OrderProductDetailsFieldset />
                            </div>

                            <div
                                className={cn('flex flex-col gap-4', showTransportToggle ? 'col-span-8' : 'col-span-9')}
                            >
                                {!isTransportOnly && <OrderSalesMaterialFieldset />}

                                <div
                                    className={cn(
                                        'grid transition-all duration-300 ease-in-out',
                                        showSalesTransport ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                                    )}
                                    aria-hidden={!showSalesTransport}
                                >
                                    <div className="overflow-hidden">
                                        <OrderSalesTransportFieldset />
                                    </div>
                                </div>
                            </div>

                            {showTransportToggle && (
                                <FieldsetColumn
                                    className="col-span-1 mt-4 items-center"
                                    label={t('forms.automaticProductsForm.labels.includeTransport')}
                                >
                                    <SwitchFieldInput
                                        {...register('isSalesTransportIncluded')}
                                        label=""
                                        size="md"
                                        disabled={formRules?.pricingStrategy?.disabled || isPurchaseTransportIncluded}
                                        dataTest="switch-sales-transport-included"
                                    />
                                </FieldsetColumn>
                            )}
                        </div>
                    </FieldsetGroup>

                    <FieldsetGroup title={t('forms.automaticProductsForm.sections.purchasePositions')}>
                        <div className="grid grid-cols-9 gap-2">
                            <div
                                className={cn('flex flex-col gap-4', showTransportToggle ? 'col-span-8' : 'col-span-9')}
                            >
                                {!isTransportOnly && <OrderPurchaseMaterialFieldset />}

                                <div
                                    className={cn(
                                        'grid transition-all duration-300 ease-in-out',
                                        showPurchaseTransport ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                                    )}
                                    aria-hidden={!showPurchaseTransport}
                                >
                                    <div className="overflow-hidden">
                                        <OrderPurchaseTransportFieldset />
                                    </div>
                                </div>
                            </div>

                            {showTransportToggle && (
                                <FieldsetColumn
                                    className="col-span-1 mt-4 items-center"
                                    label={t('forms.automaticProductsForm.labels.includeTransport')}
                                >
                                    <SwitchFieldInput
                                        {...register('isPurchaseTransportIncluded')}
                                        label=""
                                        size="md"
                                        disabled={formRules?.pricingStrategy?.disabled || !isSalesTransportIncluded}
                                        onSwitchFieldChange={handlePurchaseTransportToggle}
                                        dataTest="switch-purchase-transport-included"
                                    />
                                </FieldsetColumn>
                            )}
                        </div>
                    </FieldsetGroup>

                    {showLocations && (
                        <FieldsetGroup>
                            <div className="grid grid-cols-9 gap-2">
                                <div className={cn(showTransportToggle ? 'col-span-8' : 'col-span-9')}>
                                    <LocationsFieldset />
                                </div>
                            </div>
                        </FieldsetGroup>
                    )}

                    <FieldsetGroup>
                        <div className="grid grid-cols-9 gap-2">
                            <div className={cn(showTransportToggle ? 'col-span-8' : 'col-span-9')}>
                                <DispositionFieldset />
                            </div>
                        </div>
                    </FieldsetGroup>

                    <AdditionalServicesFieldset />
                </div>

                <WizardFooter className="mt-6">
                    <div className="flex w-full items-center justify-between">
                        {!formRules?.isSelectionClearingDisabled && (
                            <DeleteAllProductsButton onDeleteAsync={handleClearSelection} />
                        )}
                        <Button
                            className={cn('whitespace-nowrap', {
                                'ml-auto': formRules?.isSelectionClearingDisabled,
                            })}
                            type="submit"
                            data-test="proceed-button"
                            variant={mode === 'create' ? 'primary' : 'secondary'}
                            disabled={hasOpenedPanel || isSubmitDisabled}
                        >
                            {t('forms.automaticProductsForm.next')}
                        </Button>
                    </div>
                </WizardFooter>
            </form>
        </FormProvider>
    );
}
